import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Loader2, Download, Share2, Linkedin, Twitter, Copy, Check } from "lucide-react";
import { parseCV, type CVData } from "@/lib/cvParser";
import CVTemplate from "@/components/CVTemplate";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { generateCVBlob } from "@/lib/pdfGenerator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function SharedCVPage() {
  const { slug } = useParams<{ slug: string }>();
  const [cvData, setCvData] = useState<CVData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchCV = async () => {
      if (!slug) return;
        console.log('Fetching CV with slug:', slug);
        try {
          const { data, error } = await supabase
          .from('saved_cvs')
          .select('*')
          .eq('public_slug', slug)
          .eq('is_public', true)
          .maybeSingle();
        console.log('Query result:', { data, error });

        if (error) {
          throw error;
        }

        if (!data) {
          throw new Error("CV not found or not public.");
        }

        if (data.raw_result) {
          setCvData(parseCV(data.raw_result));
        } else {
          throw new Error("CV content is empty.");
        }
      } catch (err: any) {
        console.error("Error fetching shared CV:", err);
        setError(err.message || "Failed to load CV");
      } finally {
        setLoading(false);
      }
    };
    fetchCV();
  }, [slug]);

  const handleDownload = async () => {
    if (!cvData) return;
    setDownloading(true);
    try {
      const blob = await generateCVBlob(cvData, "Classic");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cvData.basicInfo?.name?.replace(/\s+/g, '_') || 'Resume'}_CV.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const shareUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !cvData) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background">
        <h1 className="text-2xl font-bold text-red-500 mb-2">Oops!</h1>
        <p className="text-muted-foreground">{error || "CV not found."}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 flex items-center justify-between border-b border-border bg-background/80 px-6 py-4 backdrop-blur-md shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-lg">
              {cvData.basicInfo?.name?.charAt(0) || "C"}
            </span>
          </div>
          <div>
            <h1 className="text-sm font-bold">{cvData.basicInfo?.name || "Shared CV"}</h1>
            <p className="text-xs text-muted-foreground">{cvData.basicInfo?.title || "Professional Profile"}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="gap-2 h-9">
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>
          <Button onClick={handleDownload} disabled={downloading} size="sm" className="gap-2 h-9">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">Download PDF</span>
          </Button>
        </div>
      </div>

      {/* CV Content (Read-only) */}
      <div className="mx-auto max-w-4xl p-6 pb-20">
        <div className="bg-white rounded-xl shadow-lg border border-border overflow-hidden pointer-events-none select-none">
          <CVTemplate data={cvData} readOnly={true} />
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share this CV</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 rounded-md border border-input bg-muted px-3 py-2 text-sm"
              />
              <Button size="icon" variant="outline" onClick={handleCopyLink}>
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank')}
              >
                <Linkedin className="h-4 w-4 text-blue-600" />
                LinkedIn
              </Button>
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=Check out this CV!`, '_blank')}
              >
                <Twitter className="h-4 w-4 text-blue-400" />
                Twitter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
